import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Trash, Pencil, UploadSimple, FileArrowDown, Download, Key, FileText, Wrench } from '@phosphor-icons/react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { usePagination } from '../components/TablePagination';
import LetterDialog from '../components/LetterDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const EmployeesPage = () => {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [users, setUsers] = useState([]);  // for create-login feature
  const [loginDialog, setLoginDialog] = useState(null);   // { emp, email, password, role, is_team_lead, extra_modules }
  const [loginResult, setLoginResult] = useState(null);   // server response after create
  const [loginSaving, setLoginSaving] = useState(false);
  const [letterEmp, setLetterEmp] = useState(null);  // employee for which to generate letters
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    emp_id: '',
    name: '',
    email: '',
    department_id: '',
    designation_id: '',
    bank_ifsc: '',
    bank_account: '',
    payment_type: 'IFT',
    joining_date: new Date().toISOString().split('T')[0],
    date_of_birth: '',
    blood_group: '',
    phone_no: '',
    emergency_phone_no: '',
    personal_email: '',
    official_email: '',
    current_address: '',
    permanent_address: '',
    aadhar_no: '',
    pan_no: '',
    is_active: true,
    left_date: '',
    profile_picture: '',
    salary_components: {
      basic: 0,
      hra: 0,
      con: 0,
      medical: 0,
      non_taxable: 0,
      special_allowance: 0,
    },
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [empRes, deptRes, desigRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/employees`, { withCredentials: true }),
        axios.get(`${API_URL}/api/departments`, { withCredentials: true }),
        axios.get(`${API_URL}/api/designations`, { withCredentials: true }),
        axios.get(`${API_URL}/api/users`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setEmployees(empRes.data);
      setDepartments(deptRes.data);
      setDesignations(desigRes.data);
      setUsers(usersRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  // Map: employee_id -> user (for showing "Has Login" badge)
  const userByEmployeeId = React.useMemo(() => {
    const m = {};
    for (const u of users) { if (u.employee_id) m[u.employee_id] = u; }
    return m;
  }, [users]);

  const openLoginDialog = (emp) => {
    const defaultEmail = (emp.email || emp.official_email || emp.personal_email
                          || `${(emp.emp_id || 'staff').toLowerCase()}@dextra.local`).toLowerCase();
    const defaultPwd = `Welcome@${(emp.emp_id || 'STAFF').toUpperCase()}`;
    setLoginDialog({
      emp, email: defaultEmail, password: defaultPwd,
      role: 'staff', is_team_lead: false, extra_modules: [],
    });
    setLoginResult(null);
  };

  const submitCreateLogin = async () => {
    if (!loginDialog) return;
    // Use the Mongo `id` if present, otherwise fall back to the human `emp_id` (e.g. EMP001).
    // Backend accepts either.
    const empKey = loginDialog.emp?.id || loginDialog.emp?.emp_id;
    if (!empKey) {
      toast.error('Internal error: this row has no id and no emp_id. Please refresh and try again.');
      return;
    }
    setLoginSaving(true);
    const url = `${API_URL}/api/employees/${encodeURIComponent(empKey)}/create-login`;
    try {
      const r = await axios.post(
        url,
        {
          email: loginDialog.email,
          password: loginDialog.password,
          role: loginDialog.role,
          is_team_lead: !!loginDialog.is_team_lead,
          extra_modules: loginDialog.extra_modules || [],
        },
        { withCredentials: true },
      );
      setLoginResult(r.data);
      toast.success(r.data?.message || 'Login created');
      // Optimistic update — push/merge the just-created/linked user into local state so the
      // "HAS LOGIN" badge flips instantly without waiting for a network round-trip. We then
      // also refresh from /api/users in the background for canonical truth.
      const linkedUser = {
        id: r.data?.id || '',
        email: r.data?.email || loginDialog.email,
        name: r.data?.name || loginDialog.emp?.name || '',
        role: r.data?.role || loginDialog.role || 'staff',
        employee_id: r.data?.employee_id || loginDialog.emp?.id || '',
        is_team_lead: !!loginDialog.is_team_lead,
        extra_modules: loginDialog.extra_modules || [],
      };
      setUsers((prev) => {
        const idx = prev.findIndex((u) => (u.email || '').toLowerCase() === (linkedUser.email || '').toLowerCase());
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...next[idx], ...linkedUser };
          return next;
        }
        return [linkedUser, ...prev];
      });
      // Background refresh to reconcile with server truth.
      axios.get(`${API_URL}/api/users`, { withCredentials: true })
        .then((u) => setUsers(u.data || []))
        .catch(() => {});
    } catch (e) {
      // Surface the full diagnostic so admins can paste it in support tickets.
      const status = e.response?.status;
      const detail = e.response?.data?.detail || e.message;
      const diagnostic = status ? `HTTP ${status} — ${detail}\nURL: ${url}` : `Request failed — ${detail}`;
      console.error('Create login error:', { url, status, detail, response: e.response?.data });
      toast.error(diagnostic, { duration: 8000 });
    } finally { setLoginSaving(false); }
  };

  const toggleLoginModule = (m) => {
    if (!loginDialog) return;
    const cur = new Set(loginDialog.extra_modules || []);
    if (cur.has(m)) cur.delete(m); else cur.add(m);
    setLoginDialog({ ...loginDialog, extra_modules: Array.from(cur) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        // Defensive guard: legacy/migrated docs occasionally have an empty `id` field,
        // which would turn the PUT URL into '/api/employees/' and trigger HTTP 405 — also
        // making letter downloads & delete fail. Auto-heal the DB first, then refresh
        // and ask the admin to re-open the row.
        if (!formData.id) {
          toast.warning('This employee record has no internal id — running database auto-heal …');
          const r = await axios.post(`${API_URL}/api/admin/maintenance/heal-empty-ids`, {}, { withCredentials: true });
          const fixed = r.data?.total_fixed || 0;
          toast.success(`Auto-heal complete — ${fixed} record(s) fixed across the DB. Reopening the row will now work. Reloading…`);
          setOpen(false);
          resetForm();
          loadData();
          return;
        }
        await axios.put(`${API_URL}/api/employees/${formData.id}`, formData, { withCredentials: true });
        toast.success(`Employee "${formData.name || formData.emp_id}" updated`);
      } else {
        await axios.post(`${API_URL}/api/employees`, formData, { withCredentials: true });
        toast.success(`Employee "${formData.name || formData.emp_id}" created`);
      }
      setOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error saving employee:', error);
      const status = error.response?.status;
      const detail = error.response?.data?.detail || error.message;
      toast.error(`Save failed${status ? ` (HTTP ${status})` : ''}: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`, { duration: 8000 });
    }
  };

  // Manual "Heal Database" button — admins call this to mint missing `id` fields across
  // ALL collections in one go. Useful when a row shows in the UI but Update/Delete/letter
  // download all silently fail because the doc's `id` field is empty.
  const [healing, setHealing] = useState(false);
  const handleHealDatabase = async () => {
    if (!window.confirm('Auto-heal scans every database table and assigns a fresh internal ID to any record that is missing one. Safe to run anytime. Continue?')) return;
    setHealing(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/maintenance/heal-empty-ids`, {}, { withCredentials: true });
      const fixed = r.data?.total_fixed || 0;
      const by = r.data?.by_collection || {};
      const detail = Object.entries(by).map(([k, v]) => `${k}: ${v}`).join(', ');
      toast.success(`Auto-heal complete — ${fixed} record(s) fixed${detail ? ` (${detail})` : ''}.`, { duration: 8000 });
      loadData();
    } catch (e) {
      toast.error('Auto-heal failed: ' + (e.response?.data?.detail || e.message));
    } finally { setHealing(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) return;
    try {
      await axios.delete(`${API_URL}/api/employees/${id}`, { withCredentials: true });
      toast.success('Employee deleted');
      loadData();
    } catch (error) {
      console.error('Error deleting employee:', error);
      toast.error('Delete failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEdit = (emp) => {
    setFormData({
      id: emp.id,
      emp_id: emp.emp_id || '',
      name: emp.name || '',
      email: emp.email || '',
      department_id: emp.department_id || '',
      designation_id: emp.designation_id || '',
      bank_ifsc: emp.bank_ifsc || '',
      bank_account: emp.bank_account || '',
      payment_type: emp.payment_type || 'IFT',
      joining_date: emp.joining_date || '',
      date_of_birth: emp.date_of_birth || '',
      blood_group: emp.blood_group || '',
      phone_no: emp.phone_no || '',
      emergency_phone_no: emp.emergency_phone_no || '',
      personal_email: emp.personal_email || '',
      official_email: emp.official_email || '',
      current_address: emp.current_address || '',
      permanent_address: emp.permanent_address || '',
      aadhar_no: emp.aadhar_no || '',
      pan_no: emp.pan_no || '',
      is_active: emp.is_active !== false,
      left_date: emp.left_date || '',
      profile_picture: emp.profile_picture || '',
      salary_components: {
        basic: emp.salary_components?.basic || 0,
        hra: emp.salary_components?.hra || 0,
        con: emp.salary_components?.con || 0,
        medical: emp.salary_components?.medical || 0,
        non_taxable: emp.salary_components?.non_taxable || 0,
        special_allowance: emp.salary_components?.special_allowance || 0,
      },
    });
    setEditMode(true);
    setOpen(true);
  };

  const resetForm = () => {
    setFormData({
      id: '',
      emp_id: '',
      name: '',
      email: '',
      department_id: '',
      designation_id: '',
      bank_ifsc: '',
      bank_account: '',
      payment_type: 'IFT',
      joining_date: new Date().toISOString().split('T')[0],
      date_of_birth: '',
      blood_group: '',
      phone_no: '',
      emergency_phone_no: '',
      personal_email: '',
      official_email: '',
      current_address: '',
      permanent_address: '',
      aadhar_no: '',
      pan_no: '',
      is_active: true,
      left_date: '',
      salary_components: {
        basic: 0,
        hra: 0,
        con: 0,
        medical: 0,
        non_taxable: 0,
        special_allowance: 0,
      },
    });
    setEditMode(false);
  };

  const getTotalSalary = (components) => {
    return Object.values(components).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  };

  const handleBulkImport = async () => {
    if (!importFile) {
      toast.error('Please select a CSV file');
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      // NOTE: do NOT manually set Content-Type — axios auto-generates the multipart boundary
      const res = await axios.post(`${API_URL}/api/employees/bulk-import`, formData, {
        withCredentials: true,
      });
      setImportResult(res.data);
      toast.success(`Imported ${res.data?.inserted || 0} employees, updated ${res.data?.updated || 0}`);
      loadData();
    } catch (error) {
      console.error('Error importing employees:', error);
      toast.error('Import failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
    }
  };

  const handleExportEmployees = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/employees/export`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'employees.csv';
      a.click();
    } catch (error) {
      console.error('Error exporting employees:', error);
    }
  };

  const downloadImportTemplate = () => {
    const headers = 'emp_id,name,email,department,designation,bank_ifsc,bank_account,joining_date,basic,hra,con,medical,non_taxable,special_allowance';
    const sample = 'EMP001,John Doe,john@company.com,Engineering,Software Engineer,KKBK0000473,1234567890,2025-01-01,30000,15000,5000,2000,3000,5000';
    const csv = `${headers}\n${sample}`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'employees_import_template.csv';
    a.click();
  };

  const [bulkLoginRunning, setBulkLoginRunning] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | inactive

  const filteredEmployees = useMemo(() => {
    const q = search.trim().toLowerCase();
    return employees.filter((emp) => {
      const isActive = emp.is_active !== false;
      if (statusFilter === 'active' && !isActive) return false;
      if (statusFilter === 'inactive' && isActive) return false;
      if (!q) return true;
      const dept = departments.find((d) => d.id === emp.department_id);
      const desig = designations.find((d) => d.id === emp.designation_id);
      const blob = `${emp.emp_id || ''} ${emp.name || ''} ${emp.email || ''} ${dept?.name || ''} ${desig?.name || ''} ${emp.phone || ''}`.toLowerCase();
      return blob.includes(q);
    });
  }, [employees, search, statusFilter, departments, designations]);
  const { paged: pagedEmployees, controls: empControls, startIdx: empStart } = usePagination(filteredEmployees, { storageKey: 'employees-page-size', testIdPrefix: 'employee' });
  const handleBulkCreateLogins = async () => {
    if (!window.confirm('Create login accounts for ALL active employees who don\'t yet have one?\n\nDefault password = Welcome@<EMP_ID>. Existing logins are skipped. A CSV with credentials will download immediately.')) return;
    setBulkLoginRunning(true);
    try {
      const r = await axios.post(`${API_URL}/api/users/bulk-create-logins`, {}, {
        withCredentials: true, responseType: 'blob',
      });
      const created = parseInt(r.headers['x-created-count'] || '0', 10);
      const skipped = parseInt(r.headers['x-skipped-count'] || '0', 10);
      const errors = parseInt(r.headers['x-error-count'] || '0', 10);
      // Save CSV
      const blob = new Blob([r.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payrollhub-logins-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${created} login(s) created · ${skipped} skipped${errors ? ` · ${errors} errors` : ''}`);
      await loadData();
    } catch (e) {
      toast.error('Bulk create failed: ' + (e.response?.data?.detail || e.message));
    }
    setBulkLoginRunning(false);
  };

  return (
    <div data-testid="employees-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Employees
          </h1>
          <p className="text-[#52525B] text-sm">Manage employee information and salary details</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={handleHealDatabase}
            disabled={healing}
            data-testid="heal-db-btn"
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
            title="Fix records with missing internal IDs (resolves HTTP 405 / failed updates / broken letter downloads)"
          >
            <Wrench size={20} className="mr-2" />
            {healing ? 'Healing…' : 'Heal DB'}
          </Button>
          <Button
            variant="outline"
            onClick={handleBulkCreateLogins}
            disabled={bulkLoginRunning}
            data-testid="bulk-create-logins-btn"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          >
            <Key size={20} className="mr-2" />
            {bulkLoginRunning ? 'Creating…' : 'Bulk Create Logins'}
          </Button>
          <Button
            variant="outline"
            onClick={handleExportEmployees}
            data-testid="export-employees-btn"
          >
            <Download size={20} className="mr-2" />
            Export CSV
          </Button>
          <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) { setImportFile(null); setImportResult(null); } }}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="bulk-import-btn">
                <UploadSimple size={20} className="mr-2" />
                Bulk Import
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Bulk Import Employees</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-sm text-sm space-y-2">
                  <p><strong>How it works:</strong></p>
                  <ul className="list-disc list-inside text-xs space-y-1">
                    <li>Upload a CSV with employee data</li>
                    <li>Existing employees (matched by emp_id) will be UPDATED</li>
                    <li>New employees will be CREATED</li>
                    <li>Missing departments/designations are auto-created</li>
                  </ul>
                </div>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={downloadImportTemplate}
                  data-testid="download-import-template-btn"
                >
                  <FileArrowDown size={18} className="mr-2" />
                  Download Template
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleExportEmployees}
                  data-testid="export-current-employees-btn"
                >
                  <Download size={18} className="mr-2" />
                  Export Current Employees (Edit & Re-import)
                </Button>
                <div className="space-y-2">
                  <Label>Select CSV File</Label>
                  <Input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setImportFile(e.target.files[0])}
                    data-testid="bulk-import-file-input"
                  />
                  {importFile && (
                    <p className="text-xs text-[#16A34A]">Selected: {importFile.name}</p>
                  )}
                </div>
                {importResult && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-sm text-sm">
                    <p className="font-semibold text-green-800">{importResult.message}</p>
                    {importResult.errors && importResult.errors.length > 0 && (
                      <div className="mt-2">
                        <p className="text-red-600 font-medium text-xs">Errors:</p>
                        <ul className="list-disc list-inside text-xs text-red-600">
                          {importResult.errors.slice(0, 5).map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setImportOpen(false)}>
                    Close
                  </Button>
                  <Button
                    onClick={handleBulkImport}
                    disabled={!importFile || importing}
                    className="bg-[#1D9E75] hover:bg-[#0F6E56]"
                    data-testid="confirm-bulk-import-btn"
                  >
                    {importing ? 'Importing...' : 'Import'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-employee-btn"
              className="bg-[#1D9E75] hover:bg-[#0F6E56] text-white rounded-sm"
            >
              <Plus size={20} className="mr-2" />
              Add Employee
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Edit Employee' : 'Add New Employee'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Profile picture uploader (top of form) */}
              <div className="flex items-center gap-4 pb-2 border-b">
                <div
                  className="w-20 h-20 rounded-full overflow-hidden flex items-center justify-center text-white text-2xl font-bold flex-shrink-0 shadow-sm"
                  style={{ background: '#1D9E75' }}
                  data-testid="employee-pic-preview"
                >
                  {formData.profile_picture
                    ? <img src={formData.profile_picture} alt="" className="w-full h-full object-cover" />
                    : <span>{(formData.name || '?').charAt(0).toUpperCase()}</span>}
                </div>
                <div className="flex-1">
                  <Label>Profile picture</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="file"
                      accept="image/*"
                      data-testid="employee-pic-input"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        e.target.value = '';
                        if (!f) return;
                        if (!f.type.startsWith('image/')) return toast.error('Please pick an image');
                        // Inline compress to 300×300 JPEG (≤ ~200 KB)
                        const reader = new FileReader();
                        reader.onload = () => {
                          const img = new Image();
                          img.onload = () => {
                            const side = Math.min(img.naturalWidth, img.naturalHeight);
                            const sx = (img.naturalWidth - side) / 2;
                            const sy = (img.naturalHeight - side) / 2;
                            const t = Math.min(300, side);
                            const c = document.createElement('canvas');
                            c.width = t; c.height = t;
                            c.getContext('2d').drawImage(img, sx, sy, side, side, 0, 0, t, t);
                            setFormData((cur) => ({ ...cur, profile_picture: c.toDataURL('image/jpeg', 0.85) }));
                          };
                          img.src = reader.result;
                        };
                        reader.readAsDataURL(f);
                      }}
                    />
                    {formData.profile_picture && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setFormData({ ...formData, profile_picture: '' })}
                        className="text-rose-600 border-rose-200 hover:bg-rose-50"
                        data-testid="employee-pic-remove"
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                  <p className="text-[10px] text-gray-500 mt-1">Auto-cropped to a 300×300 square. Stored on the employee record.</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Employee ID</Label>
                  <Input
                    value={formData.emp_id}
                    onChange={(e) => setFormData({ ...formData, emp_id: e.target.value })}
                    required
                    data-testid="employee-id-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="employee-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="employee-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Joining Date</Label>
                  <Input
                    type="date"
                    value={formData.joining_date}
                    onChange={(e) => setFormData({ ...formData, joining_date: e.target.value })}
                    required
                    data-testid="employee-joining-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Department</Label>
                  <Select
                    value={formData.department_id}
                    onValueChange={(val) => setFormData({ ...formData, department_id: val })}
                  >
                    <SelectTrigger data-testid="employee-department-select">
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map((dept) => (
                        <SelectItem key={dept.id} value={dept.id}>
                          {dept.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Designation</Label>
                  <Select
                    value={formData.designation_id}
                    onValueChange={(val) => setFormData({ ...formData, designation_id: val })}
                  >
                    <SelectTrigger data-testid="employee-designation-select">
                      <SelectValue placeholder="Select designation" />
                    </SelectTrigger>
                    <SelectContent>
                      {designations.map((desig) => (
                        <SelectItem key={desig.id} value={desig.id}>
                          {desig.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Bank IFSC</Label>
                  <Input
                    value={formData.bank_ifsc}
                    onChange={(e) => setFormData({ ...formData, bank_ifsc: e.target.value })}
                    required
                    data-testid="employee-bank-ifsc-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Bank Account</Label>
                  <Input
                    value={formData.bank_account}
                    onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })}
                    required
                    data-testid="employee-bank-account-input"
                  />
                </div>
              </div>

              {/* ─────── Personal & contact details ─────── */}
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-4 text-sm uppercase tracking-wider">Personal & Contact Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date of Birth</Label>
                    <Input
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                      data-testid="employee-dob-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Blood Group</Label>
                    <Select
                      value={formData.blood_group}
                      onValueChange={(val) => setFormData({ ...formData, blood_group: val })}
                    >
                      <SelectTrigger data-testid="employee-blood-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'].map((bg) => (
                          <SelectItem key={bg} value={bg}>{bg}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Phone No.</Label>
                    <Input
                      value={formData.phone_no}
                      onChange={(e) => setFormData({ ...formData, phone_no: e.target.value })}
                      data-testid="employee-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Emergency Phone No.</Label>
                    <Input
                      value={formData.emergency_phone_no}
                      onChange={(e) => setFormData({ ...formData, emergency_phone_no: e.target.value })}
                      data-testid="employee-emergency-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Personal Email ID</Label>
                    <Input
                      type="email"
                      value={formData.personal_email}
                      onChange={(e) => setFormData({ ...formData, personal_email: e.target.value })}
                      data-testid="employee-personal-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Official Email ID</Label>
                    <Input
                      type="email"
                      value={formData.official_email}
                      onChange={(e) => setFormData({ ...formData, official_email: e.target.value })}
                      data-testid="employee-official-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Aadhar Card No.</Label>
                    <Input
                      value={formData.aadhar_no}
                      onChange={(e) => setFormData({ ...formData, aadhar_no: e.target.value })}
                      data-testid="employee-aadhar-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>PAN Card No.</Label>
                    <Input
                      value={formData.pan_no}
                      onChange={(e) => setFormData({ ...formData, pan_no: e.target.value.toUpperCase() })}
                      data-testid="employee-pan-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Employment Status</Label>
                    <div className="flex items-center gap-3 pt-2">
                      <label className="inline-flex items-center gap-1.5 cursor-pointer text-sm">
                        <input
                          type="radio"
                          checked={formData.is_active !== false}
                          onChange={() => setFormData({ ...formData, is_active: true, left_date: '' })}
                          data-testid="employee-active-radio"
                        />
                        <span style={{ color: '#15803d', fontWeight: 600 }}>● Active</span>
                      </label>
                      <label className="inline-flex items-center gap-1.5 cursor-pointer text-sm">
                        <input
                          type="radio"
                          checked={formData.is_active === false}
                          onChange={() => setFormData({ ...formData, is_active: false, left_date: formData.left_date || new Date().toISOString().split('T')[0] })}
                          data-testid="employee-left-radio"
                        />
                        <span style={{ color: '#b91c1c', fontWeight: 600 }}>● Left</span>
                      </label>
                    </div>
                  </div>
                  {formData.is_active === false && (
                    <div className="space-y-2">
                      <Label>Date Left</Label>
                      <Input
                        type="date"
                        value={formData.left_date || ''}
                        onChange={(e) => setFormData({ ...formData, left_date: e.target.value })}
                        data-testid="employee-left-date-input"
                      />
                    </div>
                  )}
                  <div className="space-y-2 col-span-2">
                    <Label>Current Address</Label>
                    <textarea
                      className="w-full px-3 py-2 text-sm border rounded-md min-h-[60px]"
                      style={{ borderColor: 'var(--dx-border2)' }}
                      value={formData.current_address}
                      onChange={(e) => setFormData({ ...formData, current_address: e.target.value })}
                      data-testid="employee-current-address-input"
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Permanent Address</Label>
                    <textarea
                      className="w-full px-3 py-2 text-sm border rounded-md min-h-[60px]"
                      style={{ borderColor: 'var(--dx-border2)' }}
                      value={formData.permanent_address}
                      onChange={(e) => setFormData({ ...formData, permanent_address: e.target.value })}
                      data-testid="employee-permanent-address-input"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold mb-4 text-sm uppercase tracking-wider">Salary Components</h3>
                <div className="grid grid-cols-3 gap-4">
                  {Object.keys(formData.salary_components).map((key) => (
                    <div key={key} className="space-y-2">
                      <Label className="text-xs capitalize">{key.replace('_', ' ')}</Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={formData.salary_components[key] === 0 || formData.salary_components[key] === undefined
                                  ? ''
                                  : formData.salary_components[key]}
                        placeholder="0"
                        onChange={(e) => {
                          // strip non-digits (allow optional decimal point)
                          const cleaned = e.target.value.replace(/[^0-9.]/g, '');
                          setFormData({
                            ...formData,
                            salary_components: {
                              ...formData.salary_components,
                              [key]: cleaned === '' ? 0 : parseFloat(cleaned) || 0,
                            },
                          });
                        }}
                        data-testid={`salary-${key}-input`}
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-4 bg-[#F4F4F5] rounded-sm">
                  <p className="text-sm font-semibold">
                    Total Salary: ₹{getTotalSalary(formData.salary_components).toLocaleString()}
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => { setOpen(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#1D9E75] hover:bg-[#0F6E56]" data-testid="save-employee-btn">
                  {editMode ? 'Update' : 'Save'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card className="border border-[#E4E4E7]">
        <div className="p-3 flex items-center gap-3 flex-wrap border-b">
          <Input
            placeholder="Search by name, emp ID, email, dept, designation…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 max-w-md"
            data-testid="employee-search"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-sm px-2 py-2 border rounded bg-white"
            data-testid="employee-status-filter"
          >
            <option value="all">All Status</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F4F4F5]">
              <TableHead className="text-xs uppercase tracking-wider font-semibold">#</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Emp ID</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Name</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Email</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Department</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Designation</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Total Salary</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pagedEmployees.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-[#52525B]">
                  {employees.length === 0 ? 'No employees found. Add your first employee to get started.' : 'No matches.'}
                </TableCell>
              </TableRow>
            ) : pagedEmployees.map((emp, idx) => {
              const dept = departments.find((d) => d.id === emp.department_id);
              const desig = designations.find((d) => d.id === emp.designation_id);
              const isActive = emp.is_active !== false;
              return (
                <TableRow key={emp.id} data-testid={`employee-row-${emp.emp_id}`} className={isActive ? '' : 'opacity-60 bg-red-50/30'}>
                  <TableCell className="text-gray-400">{empStart + idx + 1}</TableCell>
                    <TableCell className="font-medium">{emp.emp_id}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-7 h-7 rounded-full overflow-hidden flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
                          style={{ background: '#1D9E75' }}
                          data-testid={`emp-pic-${emp.emp_id}`}
                        >
                          {emp.profile_picture
                            ? <img src={emp.profile_picture} alt="" className="w-full h-full object-cover" />
                            : (emp.name || '?').charAt(0).toUpperCase()}
                        </div>
                        <span>{emp.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-[#52525B]">{emp.email}</TableCell>
                    <TableCell>{dept?.name || 'N/A'}</TableCell>
                    <TableCell>{desig?.name || 'N/A'}</TableCell>
                    <TableCell className="font-semibold">₹{getTotalSalary(emp.salary_components).toLocaleString()}</TableCell>
                    <TableCell>
                      {isActive ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: '#dcfce7', color: '#15803d' }}>● ACTIVE</span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: '#fee2e2', color: '#b91c1c' }} title={emp.left_date ? `Left on ${emp.left_date}` : 'Left the organization'}>● LEFT</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2 items-center">
                        {userByEmployeeId[emp.id] ? (
                          <span className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800" title={`Login: ${userByEmployeeId[emp.id].email}`}>● HAS LOGIN</span>
                        ) : isActive && (
                          <>
                            <span className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-800" title="No login yet">○ NO LOGIN</span>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openLoginDialog(emp)}
                              data-testid={`create-login-${emp.emp_id}`}
                              title="Create login for this employee"
                              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            >
                              <Key size={14} className="mr-1" /> Create Login
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setLetterEmp({ ...emp, designation_name: desig?.title || desig?.name || '' })}
                          data-testid={`letter-employee-${emp.emp_id}`}
                          title="Generate Offer / Appointment Letter"
                          className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                        >
                          <FileText size={14} weight="fill" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(emp)}
                          data-testid={`edit-employee-${emp.emp_id}`}
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-[#DC2626] hover:text-[#DC2626]"
                          onClick={() => handleDelete(emp.id)}
                          data-testid={`delete-employee-${emp.emp_id}`}
                        >
                          <Trash size={16} />
                        </Button>
                      </div>
                </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {empControls}
      </Card>

      {/* Create Login Dialog */}
      {loginDialog && (
        <Dialog open={!!loginDialog} onOpenChange={(o) => { if (!o) { setLoginDialog(null); setLoginResult(null); } }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create login — {loginDialog.emp?.name}</DialogTitle>
            </DialogHeader>
            {!loginResult ? (
              <div className="space-y-3">
                <p className="text-xs text-gray-600">A login account will be created for this employee. Share the credentials with them privately.</p>
                <div><Label className="text-xs">Email *</Label>
                  <Input type="email" value={loginDialog.email}
                    onChange={(e) => setLoginDialog({ ...loginDialog, email: e.target.value })}
                    data-testid="create-login-email" />
                </div>
                <div><Label className="text-xs">Initial Password *</Label>
                  <div className="flex gap-2">
                    <Input value={loginDialog.password}
                      onChange={(e) => setLoginDialog({ ...loginDialog, password: e.target.value })}
                      data-testid="create-login-password" />
                    <Button type="button" variant="outline" size="sm"
                      onClick={() => setLoginDialog({ ...loginDialog, password: 'Welcome@' + Math.random().toString(36).slice(2, 8).toUpperCase() })}>
                      Generate
                    </Button>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1">Default: Welcome@&lt;EMP_ID&gt;. Ask the employee to change it after first login.</p>
                </div>
                <div><Label className="text-xs">Role</Label>
                  <select className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                    value={loginDialog.role} onChange={(e) => setLoginDialog({ ...loginDialog, role: e.target.value })}
                    data-testid="create-login-role">
                    <option value="staff">Staff (My Workspace only)</option>
                    <option value="team_lead">Team Lead (also sees dept tasks &amp; standups)</option>
                    <option value="proposals_user">Proposals Staff</option>
                    <option value="attendance_user">Attendance Staff</option>
                  </select>
                </div>
                <div className="border rounded-md p-2.5 bg-gray-50">
                  <Label className="text-xs font-semibold">Extra module access (optional)</Label>
                  <div className="flex flex-col gap-1 mt-1.5">
                    {['proposals', 'attendance'].map((m) => (
                      <label key={m} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={(loginDialog.extra_modules || []).includes(m)}
                          onChange={() => toggleLoginModule(m)} data-testid={`create-login-mod-${m}`} />
                        <span>{m === 'proposals' ? 'Proposals & CRM' : 'Attendance & Leaves'}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="cl-lead" checked={!!loginDialog.is_team_lead}
                    onChange={(e) => setLoginDialog({ ...loginDialog, is_team_lead: e.target.checked })}
                    data-testid="create-login-lead" />
                  <Label htmlFor="cl-lead" className="text-sm cursor-pointer">Team Lead</Label>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setLoginDialog(null)}>Cancel</Button>
                  <Button onClick={submitCreateLogin} disabled={loginSaving} className="text-white" style={{ background: 'var(--dx-primary)' }} data-testid="submit-create-login">
                    {loginSaving ? 'Creating…' : 'Create Login'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm">
                  <div className="font-bold text-emerald-900 mb-1">
                    {loginResult.already_existed ? 'Login already existed — linked to employee' : '✓ Login created'}
                  </div>
                  <p className="text-xs text-emerald-800">{loginResult.message}</p>
                </div>
                <div className="bg-gray-50 border rounded p-3 font-mono text-xs space-y-1">
                  <div><strong>Email:</strong> {loginResult.email}</div>
                  {loginResult.password && <div><strong>Password:</strong> {loginResult.password}</div>}
                  <div><strong>Role:</strong> {loginResult.role}</div>
                </div>
                {loginResult.password && (
                  <Button variant="outline" size="sm" className="w-full"
                    onClick={() => { navigator.clipboard.writeText(`Email: ${loginResult.email}\nPassword: ${loginResult.password}\nLogin URL: ${window.location.origin}/login`); toast.success('Credentials copied'); }}>
                    Copy credentials
                  </Button>
                )}
                <div className="flex justify-end pt-2">
                  <Button onClick={() => { setLoginDialog(null); setLoginResult(null); }} className="text-white" style={{ background: 'var(--dx-primary)' }}>
                    Done
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Letter Generation Dialog */}
      <LetterDialog
        employee={letterEmp}
        open={!!letterEmp}
        onClose={() => setLetterEmp(null)}
      />
    </div>
  );
};
